import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getItemFromStore } from 'helpers/utils';
import { appRoutes, hiddenRoutes } from 'routes/routes';
// import { management, adminRoutes } from 'routes/routes';

const PermissionType = {
  View: 1,
  Create: 2,
  Update: 3,
  Delete: 4
};

const PermissionOperation = {
  And: 1,
  Or: 2
};

// This is perrmission Page Name in BE
const PermissionCode = {
  SlsAreas: 'SlsAreas',
  SlsBins: 'SlsBins',
  SlsLocations: 'SlsLocations',
  SlsSites: 'SlsSites',
  SLsStocks: 'SlsStocks',
  PMSetup: 'PMSetup',
  PMUserPm: 'PMUserPm',
  PMRole: 'PMRole',
  MMMeetingDashboard: 'MMMeetingDashboard',
  MMCustFocus: 'MMCustFocus',
  MMCSMeetingBoard: 'MMCSMeetingBoard',
  MMCSActionPlan: 'MMCSActionPlan',
  BsSectorMeasurable: 'BsSectorMeasurable',
  BsSectorMeasurableData: 'BsSectorMeasurableData',
  BsSectorMeasurableTarget: 'BsSectorMeasurableTarget',
  SmArea: 'SmArea',
  SmSection: 'SmSection',
  SmSkillActivity: 'SmSkillActivity',
  SmSkillCriteria: 'SmSkillCriteria',
  SmSkillLevel: 'SmSkillLevel',
  SmSkillMaxtrix: 'SmSkillMaxtrix',
  SmSubSkill: 'SmSubSkill',
  SmSubSkillTraining: 'SmSubSkillTraining',
  SmTrainingRecord: 'SmTrainingRecord',
  JiraProjectManagement: 'JiraProjectManagement',
  Dashboard: 'Dashboard',
  DashboardQuery: 'DashboardQuery',
  SalespersonKPI: 'SalespersonKPI',
  SalespersonKPIManager: 'SalespersonKPIManager',
  ServiceDesk: 'ServiceDesk',
  ServiceDeskAdmin: 'ServiceDeskAdmin',
  Production: 'Production',
  ProductionRTLMissing: 'ProductionRTLMissing',
  ProductionOverview: 'ProductionOverview',
  LorryLoadingDashboard: 'LorryLoadingDashboard',
  EVAdminDashboard: 'EVAdminDashboard',
  EVUserDashboard: 'EVUserDashboard',
  SupplierDashboard: 'SupplierDashboard',
  EmployeeRecognition: 'EmployeeRecognition',
  EmployeeRecognitionReports: 'EmployeeRecognitionReports',
  ELVISKnowledge: 'ELVISKnowledge',
  ELVISConversations: 'ELVISConversations'
};

const usePermissionState = () => {
  const stateIsFullPermission = useSelector(
    state => state.user.isFullPermission
  );
  const statePermissions = useSelector(state => state.user.permissions);
  return useMemo(() => {
    const session = getItemFromStore('user', {}, sessionStorage);
    return {
      isFullPermission: stateIsFullPermission || session?.isFullPermission,
      permissions: statePermissions || session?.permissions
    };
  }, [stateIsFullPermission, statePermissions]);
};

const hasPermission = (route, permissionState) => {
  const { isFullPermission, permissions: permissionSetup } = permissionState;
  if (isFullPermission) return true;
  if (
    route == null ||
    route == undefined ||
    permissionSetup == null ||
    permissionSetup == undefined
  )
    return false;
  if (
    route.permission_codes !== undefined &&
    route.permission_codes !== null &&
    route.permission_codes?.length > 0
  ) {
    const existedPermissions = permissionSetup?.filter(i =>
      route.permission_codes.includes(i.pageName)
    );

    if (existedPermissions == undefined || existedPermissions.length == 0) {
      return false;
    }

    if (route.to == undefined || route.to == null) {
      return existedPermissions.some(
        i =>
          i.viewPermission ||
          i.createPermission ||
          i.updatePermission ||
          i.deletePermission
      );
    }

    const convertedPermissions = convertPermission(existedPermissions);
    const routePermissions = route.permissions;

    for (var i = 0; i < convertedPermissions.length; i++) {
      let item = convertedPermissions[i];
      let apiPermissions = item.permissions;
      if (route.permission_required_type == PermissionOperation.And) {
        if (routePermissions.every(i => apiPermissions.includes(i))) {
          return true;
        }
      } else {
        if (
          routePermissions != undefined &&
          routePermissions.some(i => apiPermissions.includes(i))
        ) {
          return true;
        }
      }
    }
    return false;
  }

  return true;
};

const hasPermissionWithPath = (path, permissionState) => {
  let strPath = path;
  if (strPath.startsWith('/')) {
    strPath = strPath.substring(1);
  }

  const hasQueryString = strPath.includes('?');
  const routeTrees = [appRoutes.children, hiddenRoutes.children];

  const findRoute = candidatePath => {
    for (const tree of routeTrees) {
      const route = findRouteByPath(tree, candidatePath);
      if (route) return route;
    }
    return null;
  };

  // Try the exact path first, then progressively drop trailing dynamic
  // segments (ids, guids, slugs, ...) until a declared route matches.
  let candidatePath = strPath;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const route = findRoute(candidatePath);
    if (route) {
      return hasPermission(route, permissionState);
    }
    if (hasQueryString || !candidatePath.includes('/')) {
      break;
    }
    candidatePath = candidatePath.substring(0, candidatePath.lastIndexOf('/'));
  }

  return false;
};

const convertPermission = permissionsData => {
  const permissions = permissionsData.map(item => {
    const {
      pageName,
      viewPermission,
      createPermission,
      updatePermission,
      deletePermission
    } = item;
    return {
      pageName,
      permissions: [
        viewPermission ? PermissionType.View : null,
        createPermission ? PermissionType.Create : null,
        updatePermission ? PermissionType.Update : null,
        deletePermission ? PermissionType.Delete : null
      ].filter(Boolean)
    };
  });
  return permissions;
};

const trimSlashes = str => {
  if (!str) return str;
  let result = str;
  if (result.startsWith('/')) result = result.substring(1);
  if (result.endsWith('/')) result = result.slice(0, -1);
  return result;
};

const findRouteByPath = (routes, path) => {
  const normalizedPath = trimSlashes(path.replace('#', ''));
  for (const route of routes) {
    if (trimSlashes(route.to) === normalizedPath) {
      return route;
    } else if (route.children) {
      const childMatch = findRouteByPath(route.children, path);
      if (childMatch) {
        return childMatch;
      }
    }
  }
  return null;
};

const buildPermissionCodeRouteMap = () => {
  const map = {};
  const walk = nodes => {
    if (!nodes) return;
    nodes.forEach(node => {
      if (node.permission_codes?.length > 0 && node.to) {
        node.permission_codes.forEach(code => {
          if (!map[code]) map[code] = [];
          map[code].push({ path: node.to, name: node.name });
        });
      }
      if (node.children) {
        walk(node.children);
      }
    });
  };
  walk(appRoutes.children);
  walk(hiddenRoutes.children);
  return map;
};

let permissionCodeRouteMap = null;

const getRoutesForPermissionCode = (code, name) => {
  if (!permissionCodeRouteMap) {
    permissionCodeRouteMap = buildPermissionCodeRouteMap();
  }
  return permissionCodeRouteMap[code] || permissionCodeRouteMap[name] || [];
};

export {
  PermissionOperation,
  PermissionType,
  PermissionCode,
  usePermissionState,
  hasPermission,
  hasPermissionWithPath,
  getRoutesForPermissionCode
};
